// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img - reused from the AIR-1000 product page
import PortalImage from 'public/img/products/Air1000/portal-dashboard.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Step = {
  title: string
  paragraphs: string[]
  /** Shown right after the first paragraph, ahead of the rest of the copy. */
  leadItems?: string[]
  /** The list that closes the step. */
  items?: string[]
  /** Short lists read better stacked; long ones default to three columns. */
  itemColumns?: 1 | 2 | 3
  /** Caveat set apart under the list. */
  note?: string
}

export default function SolutionSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const steps: Step[] = [
    {
      title: t('solution.steps.1.title'),
      paragraphs: [t('solution.steps.1.intro1'), t('solution.steps.1.intro2')],
      items: [
        t('solution.steps.1.items.01'),
        t('solution.steps.1.items.02'),
        t('solution.steps.1.items.03'),
        t('solution.steps.1.items.04'),
        t('solution.steps.1.items.05'),
        t('solution.steps.1.items.06'),
        t('solution.steps.1.items.07'),
        t('solution.steps.1.items.08'),
        t('solution.steps.1.items.09')
      ],
      note: t('solution.steps.1.note')
    },
    {
      title: t('solution.steps.2.title'),
      paragraphs: [
        t('solution.steps.2.intro1'),
        t('solution.steps.2.intro2'),
        t('solution.steps.2.intro3')
      ],
      items: [
        t('solution.steps.2.items.01'),
        t('solution.steps.2.items.02'),
        t('solution.steps.2.items.03')
      ],
      itemColumns: 1
    },
    {
      title: t('solution.steps.3.title'),
      paragraphs: [
        t('solution.steps.3.intro1'),
        t('solution.steps.3.intro2'),
        t('solution.steps.3.intro3')
      ],
      leadItems: [
        t('solution.steps.3.options.01'),
        t('solution.steps.3.options.02')
      ],
      items: [
        t('solution.steps.3.items.01'),
        t('solution.steps.3.items.02'),
        t('solution.steps.3.items.03'),
        t('solution.steps.3.items.04'),
        t('solution.steps.3.items.05'),
        t('solution.steps.3.items.06')
      ],
      note: t('solution.steps.3.note')
    },
    {
      title: t('solution.steps.4.title'),
      paragraphs: [t('solution.steps.4.intro1'), t('solution.steps.4.intro2')],
      items: [
        t('solution.steps.4.items.01'),
        t('solution.steps.4.items.02'),
        t('solution.steps.4.items.03')
      ],
      itemColumns: 1
    },
    {
      title: t('solution.steps.5.title'),
      paragraphs: [
        t('solution.steps.5.intro1'),
        t('solution.steps.5.intro2'),
        t('solution.steps.5.intro3')
      ],
      items: [
        t('solution.steps.5.items.01'),
        t('solution.steps.5.items.02'),
        t('solution.steps.5.items.03'),
        t('solution.steps.5.items.04'),
        t('solution.steps.5.items.05'),
        t('solution.steps.5.items.06')
      ],
      note: t('solution.steps.5.note')
    },
    {
      title: t('solution.steps.6.title'),
      paragraphs: [t('solution.steps.6.intro1'), t('solution.steps.6.intro2')],
      items: [
        t('solution.steps.6.items.01'),
        t('solution.steps.6.items.02'),
        t('solution.steps.6.items.03'),
        t('solution.steps.6.items.04'),
        t('solution.steps.6.items.05'),
        t('solution.steps.6.items.06'),
        t('solution.steps.6.items.07')
      ]
    }
  ]

  /**
   * A vertical rail: numbered badges down the left, the step beside them. The
   * connecting line is drawn on the badge column so it never crosses the copy.
   */
  const paragraph = (text: string) => (
    <Typography
      key={text}
      component="p"
      sx={{
        fontSize: getFontBodySmall(),
        lineHeight: 1.7,
        color: themeColors.gray.defaultText,
        marginBottom: '0.75rem',
        maxWidth: '46rem',
        '&:last-of-type': { marginBottom: 0 }
      }}
    >
      {text}
    </Typography>
  )

  const getStep = (step: Step, idx: number) => (
    <Box
      key={step.title}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: stacked ? '1rem' : '1.5rem'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: '2.5rem',
            height: '2.5rem',
            borderRadius: '50%',
            background: themeColors.success.main,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700
          }}
        >
          {idx + 1}
        </Box>

        {idx < steps.length - 1 && (
          <Box
            sx={{
              flexGrow: 1,
              width: '2px',
              marginTop: '0.5rem',
              background: themeColors.gray.lighter
            }}
          />
        )}
      </Box>

      <Box sx={{ paddingBottom: idx < steps.length - 1 ? '2.5rem' : 0 }}>
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1.15rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.75rem',
            // Centres the title against its badge.
            paddingTop: '0.35rem'
          }}
        >
          {step.title}
        </Typography>

        {/* The lead list splits the copy: the first paragraph introduces it,
            the rest of the copy continues below. */}
        {step.leadItems ? (
          <>
            {paragraph(step.paragraphs[0])}

            <Box sx={{ margin: '1rem 0 1.25rem' }}>
              <BulletGrid items={step.leadItems} columns={1} />
            </Box>

            {step.paragraphs.slice(1).map(text => paragraph(text))}
          </>
        ) : (
          step.paragraphs.map(text => paragraph(text))
        )}

        {step.items && (
          <Box sx={{ marginTop: '1.1rem' }}>
            <BulletGrid items={step.items} columns={step.itemColumns ?? 3} />
          </Box>
        )}

        {step.note && (
          <Typography
            component="p"
            sx={{
              marginTop: '1.1rem',
              paddingLeft: '0.9rem',
              borderLeft: `3px solid ${themeColors.success.main}`,
              fontSize: getFontBodySmall(),
              lineHeight: 1.7,
              color: themeColors.gray.defaultText,
              maxWidth: '46rem'
            }}
          >
            {step.note}
          </Typography>
        )}
      </Box>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('solution.eyebrow')}
        title={t('solution.title')}
        intro={[t('solution.intro1'), t('solution.intro2')]}
      />

      {/* The dashboard reads as part of the header block rather than a card
          beside it: full width, flush, and ahead of the steps. */}
      <Box
        sx={{
          lineHeight: 0,
          marginTop: '-1rem',
          marginBottom: '3rem'
        }}
      >
        <Image
          src={PortalImage}
          alt="Air quality monitoring portal showing live readings and trends"
          sizes="100vw"
          // Uncropped, and never scaled past its own pixel width.
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: PortalImage.width
          }}
        />
      </Box>

      <Box>{steps.map((step, idx) => getStep(step, idx))}</Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
